import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Snackbar, Alert, Box, Divider, CircularProgress, ToggleButtonGroup, ToggleButton, TextField, useMediaQuery, Chip, Card, CardContent, Grid, InputAdornment, Collapse, IconButton, Tooltip, Badge,  } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMutation } from "react-query";
import client from "../../../../../API";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import EditIcon from "@mui/icons-material/Edit";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import LinkIcon from "@mui/icons-material/Link";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const OfferDialog = ({ open, onClose, offer, conversationId, isCreator }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [daysSinceSent, setDaysSinceSent] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // Local state for status updates and negotiation details
  const [status, setStatus] = useState(offer?.status?.toLowerCase() || "");
  const [amount, setAmount] = useState(offer?.amount || "");
  const [revision, setRevision] = useState(offer?.revision || 0);
  const [initialStatus, setInitialStatus] = useState(offer?.status?.toLowerCase() || "");
  const [showMilestones, setShowMilestones] = useState(false);
  const [errors, setErrors] = useState({});

  // Determine UI states
  const isNegotiating = status === "counter-negotiate";
  const canToggleStatus = !["accept", "decline", "completed"].includes(
    offer?.status?.toLowerCase() || ""
  );
  const shouldShowStatusControls = isCreator || (!isCreator && offer?.status?.toLowerCase() === "counter-negotiate");
  const canComplete = offer?.status?.toLowerCase() === "accept";
  const hasStatusChanged = status !== initialStatus;
  const isCompleted = offer?.status?.toLowerCase() === "completed";

  useEffect(() => {
    if (offer && offer.created_at) {
      const sentDate = new Date(offer.created_at);
      const currentDate = new Date();
      const differenceInDays = (currentDate - sentDate) / (1000 * 3600 * 24);
      setDaysSinceSent(Math.round(differenceInDays));
    }
  }, [offer]);

  useEffect(() => {
    setInitialStatus(offer?.status?.toLowerCase() || "");
    setStatus(offer?.status?.toLowerCase() || "");
    setAmount(offer?.amount || "");
    setRevision(offer?.revision || 0);
    setErrors({});
  }, [offer]);

  const sendMessageMutation = useMutation(
    ({ conversationId, message }) =>
      client.conversations.sendMessage(conversationId, { message }),
    {
      onError: (error) => {
        console.error("Error sending message:", error);
      },
    }
  );

  const updateStatusMutation = useMutation(
    async ({ deliverableId, updateData }) => {
      const res = await client.deliverables.update(deliverableId, updateData);
      return res;
    },
    {
      onMutate: () => setLoading(true),
      onSuccess: (data, variables) => {
        setShowSuccess(true);
        setLoading(false);

        let chatMsg = "";
        const newStat = variables.updateData.status;
        if (newStat === "accept") {
          chatMsg = `We have accepted your offer.`;
        } else if (newStat === "decline") {
          chatMsg = `We have declined the offer.`;
        } else if (newStat === "counter-negotiate") {
          chatMsg = `We've proposed a counter at $${variables.updateData.amount} with ${variables.updateData.revision} revisions.`;
        }

        if (conversationId && chatMsg) {
          sendMessageMutation.mutate({
            conversationId,
            message: chatMsg,
          });
        }
        
        setTimeout(() => {
          onClose();
        }, 1500);
      },
      onError: (error) => {
        console.error("Error updating offer:", error);
        setShowError(true);
        setErrorMessage(
          `Error updating offer: ${error?.response?.data?.error || error.message}`
        );
        setLoading(false);
      },
    }
  );

  const completeOfferMutation = useMutation(
    () => client.deliverables.update(offer.id, { status: "completed" }),
    {
      onMutate: () => setLoading(true),
      onSuccess: () => {
        setShowSuccess(true);
        setLoading(false);

        if (conversationId) {
          sendMessageMutation.mutate({
            conversationId,
            message:
              "We have marked the deliverable as completed! Your payout has also been processed",
          });
        }
        
        setTimeout(() => {
          onClose();
        }, 1500);
      },
      onError: (error) => {
        console.error("Error completing offer:", error);
        setShowError(true);
        setErrorMessage(
          `Error completing offer: ${error?.response?.data?.error || error.message}`
        );
        setLoading(false);
      },
    }
  );

  const handleChangeStatus = (event, newStatus) => {
    if (newStatus !== null) {
      setStatus(newStatus);
      setErrors({});
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
      setAmount(value);
      if (errors.amount) {
        setErrors(prev => ({ ...prev, amount: null }));
      }
    }
  };

  const handleRevisionChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setRevision(value);
      if (errors.revision) {
        setErrors(prev => ({ ...prev, revision: null }));
      }
    }
  };

  const validateCounterOffer = () => {
    const newErrors = {};
    
    if (!amount || parseFloat(amount) <= 0) {
      newErrors.amount = "Please enter a valid amount";
    }
    
    if (!revision && revision !== 0) {
      newErrors.revision = "Please enter number of revisions";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitStatus = async () => {
    const updateData = { status };
    
    if (status === "counter-negotiate") {
      if (!validateCounterOffer()) {
        return;
      }
      updateData.amount = parseFloat(amount);
      updateData.revision = parseInt(revision, 10);
    }
    
    updateStatusMutation.mutate({
      deliverableId: offer.id,
      updateData,
    });
  };

  const handleCompleteOffer = () => {
    completeOfferMutation.mutate();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "accept": return "success";
      case "decline": return "error";
      case "counter-negotiate": return "warning";
      case "completed": return "success";
      case "sent": return "info";
      default: return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "accept": return <ThumbUpIcon fontSize="small" />;
      case "decline": return <ThumbDownIcon fontSize="small" />;
      case "counter-negotiate": return <CompareArrowsIcon fontSize="small" />;
      case "completed": return <CheckCircleOutlineIcon fontSize="small" />;
      default: return <InfoOutlinedIcon fontSize="small" />;
    }
  };

  const details = [
    { 
      label: "Type", 
      value: offer?.deliverable_type || "N/A",
      icon: <InfoOutlinedIcon fontSize="small" />
    },
    {
      label: "Amount",
      value: `$${offer?.amount ? parseFloat(offer.amount).toFixed(2) : "0.00"}`,
      icon: <AttachMoneyIcon fontSize="small" />
    },
    {
      label: "Due Date",
      value: offer?.due_date
        ? new Date(offer.due_date).toLocaleDateString()
        : "N/A",
      icon: <CalendarTodayIcon fontSize="small" />
    },
    {
      label: "Revisions",
      value: offer?.revision != null ? offer.revision : "N/A",
      icon: <EditIcon fontSize="small" />
    },
    {
      label: "Platforms",
      value: offer?.platforms
        ? Array.isArray(offer.platforms)
          ? offer.platforms.join(", ")
          : offer.platforms
        : "N/A",
      icon: <LinkIcon fontSize="small" />
    },
    { 
      label: "Days Since Sent", 
      value: daysSinceSent,
      icon: <CalendarTodayIcon fontSize="small" />
    },
  ];

  const renderMilestones = () => {
    if (!offer.milestones || offer.milestones.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No milestones defined for this offer.
        </Typography>
      );
    }
    
    return offer.milestones.map((milestone, idx) => (
      <Card
        key={idx}
        variant="outlined"
        sx={{
          mb: 2,
          bgcolor: "background.paper",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {milestone.description}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                Due: {new Date(milestone.due_date).toLocaleDateString()}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <AttachMoneyIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                Amount: ${parseFloat(milestone.amount).toFixed(2)}
              </Typography>
            </Grid>
          </Grid>
          {milestone.link && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <LinkIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                <a 
                  href={milestone.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: theme.palette.primary.main,
                    textDecoration: 'none'
                  }}
                >
                  {milestone.link}
                </a>
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    ));
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{ 
          sx: { 
            overflow: "hidden",
            borderRadius: 3,
            boxShadow: theme.shadows[10],
            maxWidth: { xs: "95vw", md: "700px" },
          } 
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "background.paper",
            color: "text.primary",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: { xs: 2, sm: 2.5 },
            px: { xs: 2, sm: 3 },
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <InfoOutlinedIcon color="primary" />
            <Typography variant="h6" component="div">
              Offer Details
            </Typography>
          </Box>
          
          <Chip
            icon={getStatusIcon(offer?.status)}
            label={offer?.status?.toUpperCase() || "UNKNOWN"}
            color={getStatusColor(offer?.status)}
            variant={isCompleted ? "filled" : "outlined"}
            sx={{ 
              fontWeight: 600,
              textTransform: 'uppercase',
              fontSize: '0.75rem'
            }}
          />
        </DialogTitle>

        <DialogContent
          sx={{
            bgcolor: "background.default",
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 3 },
            maxHeight: { xs: 500, md: 700 },
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {/* Main Offer Information */}
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <InfoOutlinedIcon color="info" fontSize="small" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Offer Summary
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                {details.map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box 
                      display="flex" 
                      alignItems="center"
                      sx={{
                        p: 1.5,
                        bgcolor: "background.default",
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Box 
                        sx={{ 
                          mr: 1.5,
                          color: "text.secondary",
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Box flex={1}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {item.label}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.value}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Notes Section */}
              {offer?.notes && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Brief Description:
                  </Typography>
                  <Box 
                    sx={{
                      p: 2,
                      bgcolor: "background.default",
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="body2">
                      {offer.notes}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Script Section */}
              {offer?.script && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Script Requirements:
                  </Typography>
                  <Box 
                    sx={{
                      p: 2,
                      bgcolor: "background.default",
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="body2">
                      {offer.script}
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Milestones Section */}
          {offer?.milestones && offer.milestones.length > 0 && (
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Box 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="space-between" 
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setShowMilestones(!showMilestones)}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <TaskAltIcon color="primary" fontSize="small" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Milestones & To-Do List
                    </Typography>
                    <Badge 
                      badgeContent={offer.milestones.length} 
                      color="primary"
                      sx={{ ml: 1 }}
                    />
                  </Box>
                  <IconButton
                    size="small"
                    sx={{
                      transition: "transform 0.3s",
                      transform: showMilestones ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                </Box>
                
                <Collapse in={showMilestones} timeout="auto" unmountOnExit>
                  <Box sx={{ mt: 2 }}>
                    {renderMilestones()}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            bgcolor: "background.paper",
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2.5 },
            borderTop: `1px solid ${theme.palette.divider}`,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {shouldShowStatusControls && (
            <>
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Update Offer Status
                </Typography>
                <ToggleButtonGroup
                  value={status}
                  exclusive
                  onChange={handleChangeStatus}
                  sx={{ 
                    width: '100%',
                    display: 'flex',
                    '& .MuiToggleButton-root': {
                      flex: 1,
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.5,
                      borderRadius: 2,
                    }
                  }}
                >
                  {isCreator && (
                    <>
                      <ToggleButton
                        value="accept"
                        sx={{
                          "&.Mui-selected": { 
                            bgcolor: "success.main", 
                            color: "success.contrastText",
                            "&:hover": { bgcolor: "success.dark" }
                          },
                        }}
                      >
                        <ThumbUpIcon sx={{ mr: 1 }} fontSize="small" />
                        Accept
                      </ToggleButton>
                      <ToggleButton
                        value="decline"
                        sx={{
                          "&.Mui-selected": { 
                            bgcolor: "error.main", 
                            color: "error.contrastText",
                            "&:hover": { bgcolor: "error.dark" }
                          },
                        }}
                      >
                        <ThumbDownIcon sx={{ mr: 1 }} fontSize="small" />
                        Decline
                      </ToggleButton>
                    </>
                  )}
                  {(isCreator || (!isCreator && offer?.status?.toLowerCase() === "counter-negotiate")) && (
                    <ToggleButton
                      value="counter-negotiate"
                      sx={{
                        "&.Mui-selected": { 
                          bgcolor: "warning.main", 
                          color: "warning.contrastText",
                          "&:hover": { bgcolor: "warning.dark" }
                        },
                      }}
                    >
                      <CompareArrowsIcon sx={{ mr: 1 }} fontSize="small" />
                      Counter Offer
                    </ToggleButton>
                  )}
                </ToggleButtonGroup>
              </Box>

              {/* Counter-negotiation form */}
              {isNegotiating && (
                <Card 
                  variant="outlined" 
                  sx={{ 
                    width: '100%',
                    bgcolor: "warning.light",
                    border: `2px solid ${theme.palette.warning.main}`,
                    borderRadius: 2 
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                      Counter Offer Details
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Proposed Amount"
                          value={amount}
                          onChange={handleAmountChange}
                          error={!!errors.amount}
                          helperText={errors.amount || "Enter your counter amount"}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">$</InputAdornment>
                            ),
                            inputProps: {
                              inputMode: "decimal",
                              pattern: "[0-9]*\\.?[0-9]{0,2}",
                            },
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "background.paper"
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Revisions"
                          value={revision}
                          onChange={handleRevisionChange}
                          error={!!errors.revision}
                          helperText={errors.revision || "Number of revisions included"}
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "background.paper"
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                    
                    <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                      <Typography variant="body2">
                        <strong>Note:</strong> A 5% processing fee applies to all payments.
                        {amount && parseFloat(amount) > 0 && (
                          <>
                            <br />Net amount after fees: ${(parseFloat(amount) * 0.95).toFixed(2)}
                          </>
                        )}
                      </Typography>
                    </Alert>
                  </CardContent>
                </Card>
              )}

              {/* Action Buttons */}
              <Box 
                sx={{ 
                  display: "flex", 
                  justifyContent: "flex-end", 
                  width: "100%",
                  gap: 1.5,
                  flexDirection: { xs: "column", sm: "row" }
                }}
              >
                {hasStatusChanged ? (
                  <Button 
                    variant="contained" 
                    disabled={loading} 
                    onClick={handleSubmitStatus}
                    sx={{
                      px: 4,
                      py: 1,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      minWidth: 140,
                    }}
                  >
                    {loading ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={18} color="inherit" />
                        Updating...
                      </Box>
                    ) : (
                      "Update Status"
                    )}
                  </Button>
                ) : (
                  <Button 
                    onClick={onClose} 
                    variant="outlined" 
                    sx={{ 
                      px: 3,
                      py: 1,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 500,
                    }}
                  >
                    Close
                  </Button>
                )}
              </Box>
            </>
          )}

          {/* Complete Offer Button */}
          {canComplete && (
            <Box sx={{ width: "100%" }}>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" justifyContent="flex-end">
                <Button 
                  onClick={handleCompleteOffer} 
                  variant="contained" 
                  color="success"
                  disabled={loading} 
                  startIcon={<CheckCircleOutlineIcon />}
                  sx={{ 
                    px: 4,
                    py: 1,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    minWidth: 180,
                  }}
                >
                  {loading ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={18} color="inherit" />
                      Processing...
                    </Box>
                  ) : (
                    "Mark as Completed"
                  )}
                </Button>
              </Box>
            </Box>
          )}

          {/* Close button for non-editable states */}
          {!shouldShowStatusControls && !canComplete && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
              <Button 
                onClick={onClose} 
                variant="outlined" 
                sx={{ 
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 500,
                }}
              >
                Close
              </Button>
            </Box>
          )}
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" sx={{ borderRadius: 2 }}>
          Offer updated successfully!
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default OfferDialog;